import {PureComponent} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {parseISO, parse, isValid, differenceInMonths} from 'date-fns'
import Linkedin from 'react-feather/dist/icons/linkedin.js'

import Availability from '../../Freelance/Component/Availability/Availability.jsx'
import Picture from '../../Picture/Picture.jsx'
import Price from '../../Freelance/Component/Price/Price.jsx'
import Skills from '../../Skills/Skills.jsx'
import ContactModal from '../../Modals/ContactModal/ContactModal.jsx'
import connectModal from '../../../../shared/components/Modals/connect.js'
import MapPin from 'react-feather/dist/icons/map-pin.js'
import Calendar from 'react-feather/dist/icons/calendar.js'
import Send from 'react-feather/dist/icons/send.js'
import Coins from '../../../media/svg/coins.svg'

import './FreelanceSearchIACard.scss'

const tryParseDate = (dateStr) => {
  let d = parseISO(dateStr)
  if (isValid(d)) return d
  d = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date())
  if (isValid(d)) return d
  return null
}

class FreelanceSearchIACard extends PureComponent {
  static propTypes = {
    currentUser: PropTypes.object,
    freelance: PropTypes.shape({
      id: PropTypes.number.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        fullname: PropTypes.string,
        image: PropTypes.object,
        city: PropTypes.object,
        gold: PropTypes.bool,
      }).isRequired,
      title: PropTypes.string,
      master_skills: PropTypes.array,
      expertises: PropTypes.array,
    }).isRequired,
    isAdmin: PropTypes.bool,
    matchPercentage: PropTypes.number,
    score: PropTypes.number,
    selected: PropTypes.bool,
    onOpen: PropTypes.func,
  }

  static defaultProps = {
    isAdmin: false,
    matchPercentage: null,
    score: null,
    selected: false,
    onOpen: () => {},
  }

  openContactModal = (e, user, freelance) => {
    e.stopPropagation()
    this.props.openContactModal({ user, freelance })
  }

  render() {
    const { freelance, currentUser, isAdmin, matchPercentage, score, selected, onOpen } = this.props
    const { user } = freelance

    const canViewPhoto = currentUser && (isAdmin || currentUser.account_type !== 'freelance' || currentUser.id === user.id)
    const image = canViewPhoto ? user?.image?.square ?? null : null
    const skills = (freelance?.master_skills ?? []).concat(freelance?.expertises ?? [])

    const experienceDuration = (experience) => {
      const start = tryParseDate(experience?.start)
      if (!start) return null
      const end = experience?.end ? tryParseDate(experience.end) : new Date()
      if (!end) return null

      const months = Math.max(0, differenceInMonths(end, start))
      if (months < 12) {
        return {label: `${months} mois`, iso: `P${months}M`}
      }

      const years = Math.floor(months / 12)
      return {label: `${years} an${years > 1 ? 's' : ''}`, iso: `P${years}Y`}
    }

    return (
      <div
        className={`freelance-ia-card${selected ? ' freelance-ia-card--selected' : ''}`}
        onClick={onOpen}
      >
        <div className="freelance-ia-card__header">
          <Picture
            src={image}
            alt={user?.fullname ?? ''}
            isGold={user?.gold}
            round={false}
            className="freelance-ia-card__avatar"
          />

          <div className="freelance-ia-card__informations">
            <h2 className="freelance-ia-card__fullname">
              {user?.fullname ?? ''}

              { matchPercentage !== null && (
                <span className={`badge badge-${matchPercentage > 70 ? "success" : matchPercentage > 40 ? "warning" : "danger"}`}>
                  Match idéal ~ {matchPercentage}%
                </span>
              )}

              { isAdmin && typeof score === 'number' && (
                <span className="badge badge-secondary">
                  Score ES ~ {score.toFixed(2)}
                </span>
              )}
            </h2>

            <strong className="freelance-ia-card__title">
              {freelance.title ?? ''}
            </strong>
          </div>

          <div className="freelance-ia-card__actions">
            <button
              className="btn btn-outline-secondary btn-sm justify-content-center"
              onClick={e => this.openContactModal(e, user, freelance)}
            >
              <Send size={16} />
              Contacter
            </button>

            { user?.linkedin_url && (
              <a
                href={user.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm justify-content-center"
                onClick={e => e.stopPropagation()}
              >
                <Linkedin size={16} fill="white" />
              </a>
            )}
          </div>
        </div>

        <div className="freelance-ia-card__body">
          <ul className="freelance-ia-card__more">
            <li>
              <Coins />
              <Price freelance={freelance} />
            </li>

            <li>
              <Calendar size={18} />
              Disponible <Availability freelance={freelance} />
            </li>

            { user?.city?.label ? (
              <li>
                <MapPin size={18} />
                {user?.city?.label ?? ''}
              </li>
            ) : null }
          </ul>

          { skills?.length ? (
            <>
              <p className="freelance-ia-card__subtitle">
                Expertises
              </p>

              <Skills
                length="4"
                forceNoLink={true}
                className="freelance-ia-card__skills"
                list={skills}
              />
            </>
          ) : null }

          { freelance?.experiences?.length ? (
            <>
              <p className="freelance-ia-card__subtitle">
                Dernières expériences
              </p>

              <ul className="freelance-ia-card__experiences">
                { freelance.experiences.slice(0, 3).map(experience => {
                  if (!experience.title) return null
                  const duration = experienceDuration(experience)

                  return (
                    <li key={experience.id}>
                      { experience.title }
                      { experience.company ? (<span> ∙ { experience.company }</span>) : null }
                      {duration && <time dateTime={duration.iso}> - {duration.label}</time>}
                    </li>
                  )
                })}
              </ul>
            </>
          ) : null }
        </div>
      </div>
    )
  }
}

export default connect()(connectModal({ContactModal}, FreelanceSearchIACard))
